import { ref } from 'vue';
import { useStageStore } from '@/stores/stage';
import { registerCustomFamily, unregisterCustomFamily } from '@/utils/fonts';

/**
 * Polices IMPORTÉES par l'utilisateur (upload TTF/OTF). Une fois importée, une
 * police est :
 *  1. injectée dans le document (FontFace) → utilisable par le canvas/2D ;
 *  2. ajoutée à la liste du FontPicker (stageStore.fonts) → dispo dans TOUS les
 *     champs texte ;
 *  3. persistée en IndexedDB (navigateur) → toujours là aux ré-ouvertures ;
 *  4. envoyée au service d'export (data-URL) dans le payload → incluse au ZIP et
 *     utilisée pour le rendu serveur (registerFont exige du TTF/OTF, d'où la
 *     restriction de format).
 *
 * Persistance : IndexedDB natif (même convention que useUploadHistory /
 * useProofLogo — PAS d'idb-keyval), plusieurs entrées (clé = family).
 */
export interface CustomFont {
    /** Nom de famille (dérivé du nom de fichier) — clé unique + fontFamily. */
    family: string;
    /** Données de la police en data-URL (persistée + envoyée à l'export). */
    dataUrl: string;
    /** 'truetype' | 'opentype' (hint FontFace / extension). */
    format: 'truetype' | 'opentype';
    ts: number;
}

const DB_NAME = 'pxl-custom-fonts';
const STORE = 'fonts';
const DB_VERSION = 1;
export const MAX_FONT_BYTES = 5 * 1024 * 1024; // 5 Mo : borne le payload d'export

// État module (singleton) : la liste et le flag « chargé » sont partagés entre
// tous les FontPicker.
const customFonts = ref<CustomFont[]>([]);
let loaded = false;
let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
    if (dbPromise) {
        return dbPromise;
    }
    dbPromise = new Promise((resolve) => {
        try {
            if (typeof indexedDB === 'undefined') {
                resolve(null);
                return;
            }
            const req = indexedDB.open(DB_NAME, DB_VERSION);
            req.onupgradeneeded = () => {
                const db = req.result;
                if (!db.objectStoreNames.contains(STORE)) {
                    db.createObjectStore(STORE, { keyPath: 'family' });
                }
            };
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => resolve(null);
        } catch {
            resolve(null);
        }
    });
    return dbPromise;
}

async function idbGetAll(): Promise<CustomFont[]> {
    const db = await openDb();
    if (!db) {
        return [];
    }
    return new Promise((resolve) => {
        try {
            const req = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
            req.onsuccess = () => resolve(Array.isArray(req.result) ? (req.result as CustomFont[]) : []);
            req.onerror = () => resolve([]);
        } catch {
            resolve([]);
        }
    });
}

async function idbPut(rec: CustomFont): Promise<void> {
    const db = await openDb();
    if (!db) {
        return;
    }
    await new Promise<void>((resolve) => {
        try {
            const tx = db.transaction(STORE, 'readwrite');
            tx.objectStore(STORE).put(rec);
            tx.oncomplete = () => resolve();
            tx.onerror = () => resolve();
        } catch {
            resolve();
        }
    });
}

async function idbDelete(family: string): Promise<void> {
    const db = await openDb();
    if (!db) {
        return;
    }
    await new Promise<void>((resolve) => {
        try {
            const tx = db.transaction(STORE, 'readwrite');
            tx.objectStore(STORE).delete(family);
            tx.oncomplete = () => resolve();
            tx.onerror = () => resolve();
        } catch {
            resolve();
        }
    });
}

function readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = () => reject(new Error('read error'));
        r.readAsDataURL(file);
    });
}

/** Nom de famille propre dérivé du nom de fichier (sans extension). */
function familyFromFileName(name: string): string {
    return (name || 'Police')
        .replace(/\.(ttf|otf)$/i, '')
        .replace(/[_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 60);
}

/** Détecte le format depuis l'extension (seuls TTF/OTF sont acceptés). */
function formatFromFileName(name: string): 'truetype' | 'opentype' | null {
    if (/\.ttf$/i.test(name)) {
        return 'truetype';
    }
    if (/\.otf$/i.test(name)) {
        return 'opentype';
    }
    return null;
}

/** Injecte une police dans le document via FontFace (idempotent par famille). */
async function activateFontFace(rec: CustomFont): Promise<void> {
    try {
        if (typeof FontFace === 'undefined' || !document.fonts) {
            return;
        }
        // Évite les doublons si déjà présente (ré-appel de load).
        for (const ff of document.fonts as unknown as Iterable<FontFace>) {
            if (ff.family === rec.family) {
                return;
            }
        }
        const face = new FontFace(rec.family, `url(${rec.dataUrl}) format('${rec.format}')`);
        const loadedFace = await face.load();
        // `add` : la lib DOM du projet type FontFaceSet sans add/delete → cast.
        (document.fonts as unknown as { add(f: FontFace): void }).add(loadedFace);
    } catch (e) {
        console.warn('[custom-fonts] activation FontFace échouée', rec.family, e);
    }
}

/** Ajoute la famille à la liste du FontPicker (dédupliquée), en tête. */
function addToPickerList(family: string): void {
    const stageStore = useStageStore();
    if (!stageStore.fonts.includes(family)) {
        // En tête : l'utilisateur retrouve sa police tout de suite.
        stageStore.fonts = [family, ...stageStore.fonts];
    }
}

function removeFromPickerList(family: string): void {
    const stageStore = useStageStore();
    stageStore.fonts = stageStore.fonts.filter((f) => f !== family);
}

export function useCustomFonts() {
    /** Charge + active toutes les polices importées (au démarrage de l'app). */
    async function loadCustomFonts(): Promise<void> {
        if (loaded) {
            // Ré-appel : on s'assure quand même que le picker/les FontFace sont à jour
            // (ex. store re-réinitialisé au changement de produit).
            for (const rec of customFonts.value) {
                registerCustomFamily(rec.family);
                addToPickerList(rec.family);
                void activateFontFace(rec);
            }
            return;
        }
        const all = await idbGetAll();
        customFonts.value = all.sort((a, b) => a.family.localeCompare(b.family));
        for (const rec of customFonts.value) {
            registerCustomFamily(rec.family);
            addToPickerList(rec.family);
            void activateFontFace(rec);
        }
        loaded = true;
    }

    /**
     * Importe une police depuis un fichier (TTF/OTF). Rejette les autres formats
     * (registerFont/opentype côté export exigent TTF/OTF). Renvoie la famille.
     */
    async function addFromFile(file: File): Promise<string> {
        const format = formatFromFileName(file.name);
        if (!format) {
            throw new Error('UNSUPPORTED_FORMAT');
        }
        if (file.size > MAX_FONT_BYTES) {
            throw new Error('TOO_LARGE');
        }
        const dataUrl = await readFileAsDataUrl(file);
        const family = familyFromFileName(file.name);
        const rec: CustomFont = { family, dataUrl, format, ts: Date.now() };

        await activateFontFace(rec);
        registerCustomFamily(family);
        addToPickerList(family);
        trackOnce('feature_used', { feature: 'custom_font_upload' }, 'feature:custom_font_upload');
        trackEvent('custom_font_uploaded', { format });
        // Remplace une éventuelle entrée de même famille (ré-upload).
        customFonts.value = [rec, ...customFonts.value.filter((f) => f.family !== family)].sort((a, b) =>
            a.family.localeCompare(b.family)
        );
        await idbPut(rec);
        return family;
    }

    async function removeCustomFont(family: string): Promise<void> {
        customFonts.value = customFonts.value.filter((f) => f.family !== family);
        unregisterCustomFamily(family);
        removeFromPickerList(family);
        try {
            // Retire la FontFace active (best-effort).
            const fontSet = document.fonts as unknown as Iterable<FontFace> & { delete(f: FontFace): void };
            for (const ff of fontSet) {
                if (ff.family === family) {
                    fontSet.delete(ff);
                }
            }
        } catch {
            /* noop */
        }
        await idbDelete(family);
    }

    /**
     * Polices custom à joindre au payload d'export. Si `usedFamilies` est fourni,
     * on ne renvoie QUE celles réellement utilisées dans le design (payload léger).
     */
    function getCustomFontsPayload(usedFamilies?: Set<string> | string[]): {
        family: string;
        dataUrl: string;
        format: string;
    }[] {
        const used = usedFamilies ? new Set(usedFamilies) : null;
        return customFonts.value
            .filter((f) => !used || used.has(f.family))
            .map((f) => ({ family: f.family, dataUrl: f.dataUrl, format: f.format }));
    }

    return { customFonts, loadCustomFonts, addFromFile, removeCustomFont, getCustomFontsPayload };
}
